import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';
import { Button, Box, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../components/DatePicker';
import DropdownSelect from '../../components/DropdownSelect';
import Loader from '../../components/Loader';
import {
  academicYears,
  defaultAcademicYear
} from '../../constants/academicYears';
import { RootState } from '../../redux/store';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import { downloadReport } from '../../utils/reportUtils';
import PendingFeeReportTable, {
  PendingFeeRecord
} from './PendingFeeReportTable';
import { DownloadButtonSection } from './styled';
import { PageWrapper, PaperWrapper, Title } from '../common/styled';
import { SearchInputWrapper } from '../PendingFeeSummary/styled';

interface Props {
  data: PendingFeeRecord[] | null;
  allClasses: SchoolClassesConfig;
  onSearch: (
    academicYear: string,
    grade: string,
    division: string,
    asOnDate: string
  ) => void;
  loading: boolean;
}

const PendingFeeReport: React.FC<Props> = ({
  data,
  allClasses,
  onSearch,
  loading
}) => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [academicYear, setAcademicYear] = useState('');
  const [allDivisions, setAllDivisions] = useState<
    { label: string; value: string }[]
  >([]);
  const defaultDropDownOption = { label: 'All', value: 'All' };
  const [grade, setGrade] = useState('All');
  const [division, setDivision] = useState('All');
  const [asOnDate, setAsOnDate] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    setAcademicYear(defaultAcademicYear || '');
  }, []);

  const allGrades = (allClasses?.grades || []).map((grade) => ({
    label: grade,
    value: grade
  }));

  const onGradeChange = (
    event:
      | React.ChangeEvent<{ value: unknown }>
      | React.ChangeEvent<HTMLInputElement>
      | any
  ) => {
    const selectedGrade = event.target.value as string;
    setGrade(selectedGrade);
    const divisions = allClasses.classes
      .filter((schoolClass) => schoolClass.grade === selectedGrade)
      .map((schoolClass) => ({
        label: schoolClass.division,
        value: schoolClass.division
      }));
    setAllDivisions(divisions);
  };

  const academicYearsList = academicYears.map((year) => ({
    label: year.label,
    value: year.value
  }));

  const handleDownload = () => {
    if (!data || data.length === 0) {
      downloadReport([], 'PendingFeeReport', 'Pending Fee Report', () => {});
      return;
    }

    const rows = data.map((row) => ({
      'Sr. No.': row.srNo,
      Enrollment: row.enrollmentNo,
      'Student Name': row.studentName,
      'Student Type': row.studentType,
      'Student Status': row.studentStatus,
      Grade: row.grade,
      Division: row.division,
      'Academic Year': row.academicYear,
      "Father's Name": row.fathersName,
      'Contact No.': row.contactNo,
      'Fee Type': row.feeType,
      'Fee Period': row.feePeriod,
      'Payment Due Date': row.paymentDueDate,
      Amount: row.amount,
      'Late Fee': row.lateFee,
      'Bounce Charges': row.bounceCharges
    }));

    const fileName = `${loggedInUser.school}-PendingFeeDetailReport-AsOn-${
      asOnDate ? asOnDate.format('DD-MM-YYYY') : ''
    }`;

    downloadReport(rows as any[], fileName, 'Pending Fee Report', () => {});
  };

  return (
    <>
      <Title>Pending Fee Report</Title>
      <PaperWrapper>
        <SearchInputWrapper>
          <Box sx={{ minWidth: 220 }}>
            <DropdownSelect
              name='academicYear'
              placeholder='Select Academic Year'
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value as string)}
              label='Academic Year'
              options={[defaultDropDownOption, ...academicYearsList]}
            />
          </Box>

          <Box sx={{ minWidth: 220 }}>
            <DropdownSelect
              name='grade'
              placeholder='Select Grade'
              value={grade}
              onChange={onGradeChange}
              label='Grade'
              options={[defaultDropDownOption, ...allGrades]}
            />
          </Box>

          <Box sx={{ minWidth: 220 }}>
            <DropdownSelect
              name='division'
              placeholder='Select Division'
              value={division}
              onChange={(e) => setDivision(e.target.value as string)}
              label='Division'
              options={[defaultDropDownOption, ...allDivisions]}
            />
          </Box>

          <Box sx={{ minWidth: 220 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CustomDatePicker
                label='As On Date'
                value={asOnDate}
                onChange={(newValue) => setAsOnDate(newValue)}
                width='100%'
              />
            </LocalizationProvider>
          </Box>
          <Box
            sx={{
              display: 'flex',
              width: '150px',
              '@media(min-width: 1230px)': {
                flex: 1,
                justifyContent: 'end'
              }
            }}
          >
            <Button
              variant='contained'
              onClick={() =>
                onSearch(
                  academicYear,
                  grade,
                  division,
                  asOnDate ? asOnDate.format('YYYY-MM-DD') : ''
                )
              }
            >
              {loading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'View'
              )}
            </Button>
          </Box>
        </SearchInputWrapper>
      </PaperWrapper>
      <PageWrapper sx={{ display: loading || data ? '' : 'none' }}>
        {loading ? (
          <Loader show showFullScreen />
        ) : (
          data && (
            <Box>
              <DownloadButtonSection>
                <Button variant='contained' onClick={handleDownload}>
                  Download
                </Button>
              </DownloadButtonSection>
              <PendingFeeReportTable data={data || []} />
            </Box>
          )
        )}
      </PageWrapper>
    </>
  );
};

export default PendingFeeReport;
